import { ErrorBoundary, Skeleton } from '@commander/ui';
import { safeChunkReload } from '@commander/utils';
import { type ComponentType, lazy, type ReactNode, Suspense } from 'react';

/**
 * Enhanced React.lazy wrapper that handles dynamic module import failures
 * (e.g. 404/MIME type mismatches after new deployments) by automatically
 * triggering a single hard reload to fetch fresh index.html & JS assets.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      console.warn('[lazyWithRetry] Dynamic module import failed:', error);
      if (safeChunkReload()) {
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    }
  });
}

interface LazyBoundaryProps {
  children: ReactNode;
  /** Optional custom fallback. If not provided, uses default skeleton loader */
  fallback?: ReactNode;
  /** Optional feature name for error reporting */
  featureName?: string;
}

/**
 * Reusable Suspense and Error boundary for lazy-loaded components.
 * Provides consistent loading state, prevents layout shift, and catches chunk loading errors.
 */
export function LazyBoundary({
  children,
  fallback,
  featureName = 'Lazy Module',
}: LazyBoundaryProps) {
  const defaultFallback = (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="space-y-4 w-full max-w-2xl">
        <Skeleton className="h-12 w-[250px]" />
        <Skeleton className="h-4 w-[300px]" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    </div>
  );

  return (
    <ErrorBoundary featureName={featureName}>
      <Suspense fallback={fallback ?? defaultFallback}>{children}</Suspense>
    </ErrorBoundary>
  );
}
